import React, { useState, useEffect, useRef } from 'react';
import CustomSelect from '../CustomSelect';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useTranslation } from '../../i18n/LanguageContext';
import { useAuth } from '../../App';
import { IDEA_CATEGORIES_LIST } from './IdeasNew';
import { Home, Lightbulb, Search, RefreshCw, ThumbsUp, ThumbsDown, MessageCircle, Eye, ArrowRight, Loader2 } from 'lucide-react';

// YouTube-style view count: 1000 → 1K, 1500000 → 1.5M
function formatViewCount(n) {
  const num = Number(n) || 0;
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(num);
}

function IdeasList({ onNavigate }) {
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState({ status: '', category_id: '', sort: 'recent', search: '' });
  const [loading, setLoading] = useState(true);
  const [votingIdeaId, setVotingIdeaId] = useState(null);
  const [statusDropdownIdeaId, setStatusDropdownIdeaId] = useState(null);
  const [updatingStatusIdeaId, setUpdatingStatusIdeaId] = useState(null);
  const navigate = useNavigate();

  const getStatusLabel = (opt) => (t(opt.labelKey) && t(opt.labelKey) !== opt.labelKey ? t(opt.labelKey) : opt.fallback);
  const updateStatusButtonText = (t('ideas.update_status') && t('ideas.update_status') !== 'ideas.update_status') ? t('ideas.update_status') : 'Update status';

  const getCategoryDisplayName = (categoryName) => {
    if (!categoryName) return '';
    const opt = IDEA_CATEGORIES_LIST.find(c => c.value === categoryName);
    return opt ? t(opt.labelKey) : categoryName;
  };

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadData();
  }, [filter]);

  const canUpdateStatus = user?.role === 'admin';

  const loadData = async (showFullLoading = true) => {
    try {
      if (showFullLoading) setLoading(true);
      const token = localStorage.getItem('token');
      const ideasUrl = `${API_BASE}/api/ideas/ideas?${new URLSearchParams({
        ...Object.fromEntries(Object.entries(filter).filter(([_, v]) => v !== '')),
      })}`;
      const categoriesUrl = `${API_BASE}/api/ideas/categories`;
      const headers = { Authorization: `Bearer ${token}` };

      // Load ideas and categories in parallel (faster than one after the other)
      const [ideasRes, categoriesRes] = await Promise.all([
        fetch(ideasUrl, { headers }),
        fetch(categoriesUrl, { headers })
      ]);

      if (!ideasRes.ok) {
        const errorText = await ideasRes.text();
        throw new Error(t('ideas.error_loading_ideas'));
      }
      if (!categoriesRes.ok) {
        const errorText = await categoriesRes.text();
        throw new Error(t('ideas.error_loading_categories'));
      }

      const [ideasData, categoriesData] = await Promise.all([
        ideasRes.json(),
        categoriesRes.json()
      ]);

      setIdeas(ideasData.ideas || []);
      setCategories(categoriesData.categories || []);
    } catch (error) {
      console.error('❌ [IDEAS] Erreur chargement idées:', error);
      console.error('❌ [IDEAS] Stack:', error.stack);
      if (showFullLoading) alert(t('ideas.error_loading_data'));
    } finally {
      if (showFullLoading) setLoading(false);
    }
  };

  const handleVote = async (ideaId, voteType) => {
    try {
      setVotingIdeaId(ideaId);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/ideas/ideas/${ideaId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ vote_type: voteType })
      });

      if (!res.ok) {
        throw new Error(t('ideas.error_voting'));
      }

      await loadData(false);
    } catch (error) {
      console.error('Erreur vote:', error);
      alert(t('ideas.error_voting'));
    } finally {
      setVotingIdeaId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      submitted: { labelKey: 'ideas.status_submitted', color: '#3B82F6' },
      under_review: { labelKey: 'ideas.status_under_review', color: '#F59E0B' },
      approved: { labelKey: 'ideas.status_approved', color: '#10B981' },
      in_progress: { labelKey: 'ideas.status_in_progress', color: '#8B5CF6' },
      implemented: { labelKey: 'ideas.status_implemented', color: '#059669' },
      rejected: { labelKey: 'ideas.status_rejected', color: '#EF4444' },
      archived: { labelKey: 'ideas.status_archived', color: '#6B7280' }
    };
    const statusKey = (status || '').toString().toLowerCase().replace(/\s+/g, '_');
    const { labelKey, color } = statusMap[statusKey] || { labelKey: null, color: '#6B7280' };
    const label = labelKey ? t(labelKey) : status;

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#ffffff',
        backgroundColor: color
      }}>
        {label}
      </span>
    );
  };

  const STATUS_OPTIONS = [
    { code: 'submitted', labelKey: 'ideas.status_submitted', fallback: 'Submitted' },
    { code: 'under_review', labelKey: 'ideas.status_under_review', fallback: 'Under review' },
    { code: 'approved', labelKey: 'ideas.status_approved', fallback: 'Approved' },
    { code: 'in_progress', labelKey: 'ideas.status_in_progress', fallback: 'In progress' },
    { code: 'implemented', labelKey: 'ideas.status_implemented', fallback: 'Implemented' },
    { code: 'rejected', labelKey: 'ideas.status_rejected', fallback: 'Rejected' },
    { code: 'archived', labelKey: 'ideas.status_archived', fallback: 'Archived' }
  ];

  const handleUpdateStatus = async (idea, nextStatus) => {
    if (!canUpdateStatus || !nextStatus) return;
    try {
      setUpdatingStatusIdeaId(idea.id);
      setStatusDropdownIdeaId(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/ideas/ideas/${idea.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: nextStatus })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update status');
      }
      await loadData(false);
    } catch (err) {
      console.error('❌ [IDEAS] Status update failed:', err);
      window.alert(t('ideas.status_update_error') || 'Could not update idea status.');
    } finally {
      setUpdatingStatusIdeaId(null);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent comme HomePage */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('common.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{
            fontSize: isMobile ? '24px' : 'clamp(28px, 5vw, 40px)',
            fontWeight: 700,
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Lightbulb size={isMobile ? 26 : 32} color="#fbbf24" style={{ flexShrink: 0 }} />
            <span>{t('ideas.title')}</span>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="ideas-content" style={{
        paddingTop: 'clamp(100px, 12vw, 140px)',
        paddingBottom: '40px',
        paddingLeft: 'clamp(16px, 4vw, 40px)',
        paddingRight: 'clamp(16px, 4vw, 40px)',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Submit an idea button - in body above filters */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate('/ideas/new');
          }}
          style={{
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: '#ffffff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            maxWidth: isMobile ? '100%' : '280px',
            marginBottom: '24px',
            transition: 'all 0.3s',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
          }}
        >
          + {t('ideas.submit_idea')}
        </button>

        {/* Filtres et recherche */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
            gap: '12px',
            alignItems: 'center'
          }}>
            {/* Recherche */}
            <div style={{
              position: 'relative',
              gridColumn: isMobile ? '1' : '1'
            }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'rgba(255, 255, 255, 0.5)',
                pointerEvents: 'none'
              }} />
              <input
                type="text"
                placeholder={t('ideas.search_placeholder')}
                value={filter.search}
                onChange={(e) => setFilter({...filter, search: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 44px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
              />
            </div>

            {/* Filtre Statut */}
            <CustomSelect
              value={filter.status}
              onChange={(val) => setFilter({...filter, status: val})}
              options={[
                { value: '', label: t('ideas.all_statuses') },
                { value: 'submitted', label: t('ideas.status_submitted') },
                { value: 'under_review', label: t('ideas.status_under_review') },
                { value: 'approved', label: t('ideas.status_approved') },
                { value: 'in_progress', label: t('ideas.status_in_progress') },
                { value: 'implemented', label: t('ideas.status_implemented') }
              ]}
              style={{
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                minWidth: 140
              }}
            />

            {/* Filtre Catégorie */}
            <CustomSelect
              value={filter.category_id}
              onChange={(val) => setFilter({...filter, category_id: val})}
              options={[
                { value: '', label: t('ideas.all_categories') },
                ...categories.map(cat => ({ value: String(cat.id), label: getCategoryDisplayName(cat.name) }))
              ]}
              style={{
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                minWidth: 140
              }}
            />

            {/* Filtre Tri */}
            <CustomSelect
              value={filter.sort}
              onChange={(val) => setFilter({...filter, sort: val})}
              options={[
                { value: 'recent', label: t('ideas.sort_recent') },
                { value: 'popular', label: t('ideas.sort_popular') },
                { value: 'comments', label: t('ideas.sort_comments') }
              ]}
              style={{
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                minWidth: 140
              }}
            />
          </div>
        </div>

        {/* Grille d'idées */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px'
          }}>
            <RefreshCw size={32} style={{ margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
            {t('common.loading') || 'Chargement...'}
          </div>
        ) : ideas.length === 0 ? (
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            padding: '60px 40px',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <Lightbulb size={64} style={{ margin: '0 auto 24px', opacity: 0.5, color: '#fbbf24' }} />
            <h3 style={{
              fontSize: '24px',
              fontWeight: 700,
              marginBottom: '12px',
              color: '#ffffff'
            }}>
              {t('ideas.no_ideas')}
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '32px'
            }}>
              {t('ideas.be_first')} 💡
            </p>
            <button
              onClick={(e) => {
              console.log('🔍 [IDEAS] Clic sur bouton "+ Submit an idea"');
              e.preventDefault();
              e.stopPropagation();
              navigate('/ideas/new');
            }}
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#ffffff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              + {t('ideas.submit_idea')}
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '24px'
          }}>
            {ideas.map(idea => (
              <div
                key={idea.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  padding: '24px',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)';
                }}
                onClick={(e) => {
                  console.log('🔍 [IDEAS] Clic sur carte idée:', idea.id);
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/ideas/${idea.id}`);
                }}
              >
                {/* Header avec badges */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px',
                  flexWrap: 'wrap'
                }}>
                  {idea.category_name && (
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#ffffff',
                      backgroundColor: idea.category_color || '#6B7280'
                    }}>
                      {getCategoryDisplayName(idea.category_name)}
                    </span>
                  )}
                  {getStatusBadge(idea.status)}
                </div>

                {/* Titre */}
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  margin: '0 0 12px 0',
                  color: '#ffffff',
                  lineHeight: 1.3
                }}>
                  {idea.title}
                </h3>

                {/* Description */}
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  marginBottom: '20px'
                }}>
                  {idea.description.substring(0, 150)}
                  {idea.description.length > 150 && '...'}
                </p>

                {/* Meta */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  marginBottom: '20px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <span>
                    {idea.is_anonymous ? `👤 ${t('ideas.anonymous')}` : `👤 ${idea.first_name || ''} ${idea.last_name || ''}`}
                  </span>
                  <span>
                    📅 {new Date(idea.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                  </span>
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  {/* Votes */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative', minHeight: '32px' }}>
                      {votingIdeaId === idea.id ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                          <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                          {t('common.loading') || 'Loading...'}
                        </span>
                      ) : (
                        <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVote(idea.id, 'up');
                        }}
                        style={{
                          padding: '6px 12px',
                          background: idea.user_vote === 'up' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.1)',
                          border: `1px solid ${idea.user_vote === 'up' ? '#10b981' : 'rgba(255, 255, 255, 0.2)'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (idea.user_vote !== 'up') {
                            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.25)';
                            e.currentTarget.style.borderColor = '#10b981';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (idea.user_vote !== 'up') {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                          }
                        }}
                        title={t('ideas.vote_up')}
                      >
                        <ThumbsUp size={16} fill={idea.user_vote === 'up' ? 'currentColor' : 'none'} />
                        {idea.upvotes ?? 0}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVote(idea.id, 'down');
                        }}
                        style={{
                          padding: '6px 12px',
                          background: idea.user_vote === 'down' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(255, 255, 255, 0.1)',
                          border: `1px solid ${idea.user_vote === 'down' ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (idea.user_vote !== 'down') {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.25)';
                            e.currentTarget.style.borderColor = '#ef4444';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (idea.user_vote !== 'down') {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                          }
                        }}
                        title={t('ideas.vote_down')}
                      >
                        <ThumbsDown size={16} fill={idea.user_vote === 'down' ? 'currentColor' : 'none'} />
                        {idea.downvotes ?? 0}
                      </button>
                        </>
                      )}
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.7)',
                        marginLeft: '8px'
                      }}>
                        {t('ideas.score')}: {idea.score ?? 0}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MessageCircle size={14} />
                        {idea.comments_count ?? 0}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title={(idea.views_count ?? 0) + ' ' + (t('ideas.views') || 'views')}>
                        <Eye size={14} />
                        {formatViewCount(idea.views_count ?? 0)} {t('ideas.views') || 'views'}
                      </span>
                    </div>
                  </div>

                  {/* Boutons d'actions bas de carte */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: canUpdateStatus ? 'column' : 'row',
                      gap: '8px',
                      width: '100%'
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ideas/${idea.id}`);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb, #1d4ed8)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6, #2563eb)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {t('ideas.view_details')}
                      <ArrowRight size={16} />
                    </button>
                    {canUpdateStatus && (
                      <>
                        {statusDropdownIdeaId !== idea.id ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatusDropdownIdeaId(idea.id);
                            }}
                            disabled={updatingStatusIdeaId === idea.id}
                            style={{
                              width: '100%',
                              padding: '9px 16px',
                              background: 'transparent',
                              color: '#e5e7eb',
                              border: '1px solid rgba(255,255,255,0.3)',
                              borderRadius: '10px',
                              cursor: updatingStatusIdeaId === idea.id ? 'wait' : 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (updatingStatusIdeaId !== idea.id) {
                                e.currentTarget.style.background = 'rgba(59,130,246,0.15)';
                                e.currentTarget.style.borderColor = '#3b82f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                            }}
                          >
                            {updatingStatusIdeaId === idea.id ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : null}
                            {updateStatusButtonText}
                          </button>
                        ) : (
                          <div style={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
                            <select
                              value={idea.status || 'submitted'}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val) handleUpdateStatus(idea, val);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                background: 'rgba(15, 23, 42, 0.95)',
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,0.3)',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer'
                              }}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.code} value={opt.code}>
                                  {getStatusLabel(opt)}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusDropdownIdeaId(null);
                              }}
                              style={{
                                marginTop: '6px',
                                width: '100%',
                                padding: '6px',
                                background: 'transparent',
                                color: 'rgba(255,255,255,0.6)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '8px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSS pour animations et responsive */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .ideas-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .ideas-content {
            padding: 180px 60px 60px !important;
          }
        }
        .ideas-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}

export default IdeasList;